import React, { useEffect, useState, useRef } from 'react';
import { getSubmissionStatusColor, getOutcomeConfig } from '../../utils/statusBadge';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Button,
  Spinner,
  Badge,
  SimpleGrid,
  Image as ChakraImage,
  useDisclosure,
  useToast,
  Container,
  Icon,
  Divider,
  Textarea,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { FaUser, FaVial, FaFileAlt, FaMapMarkerAlt, FaPhone, FaSms, FaLocationArrow, FaClipboardCheck, FaCalendarAlt, FaStickyNote, FaUserMd, FaRoute, FaMapPin, FaBox, FaSyringe, FaCheck, FaEllipsisH, FaCheckCircle } from 'react-icons/fa';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PhlebTour from './PhlebTour';
import { supabase } from '../../supabaseClient';
import DocumentPreview from '../shared/DocumentPreview';
import OutcomeDrawer from './OutcomeDrawer';
import { tagColor } from '../../utils/submissionTags';
import { logSubmissionOutcome } from '../../utils/logOutcome';
import { formatSchedule } from '../../utils/schedule';
import { lookupTubesForTests, parseTubeGuide, tubeGuideSig } from '../../utils/labaideTube';
import TubeGuide from '../shared/TubeGuide';

// The happy-path visit progression. One tap advances to the next step; the
// draw step opens an inline note + result panel instead of advancing blindly.
// Codes line up with OUTCOME_CONFIG so the data matches the OutcomeDrawer.
const VISIT_STEPS = [
  { code: 'en_route',           label: 'En route',     cta: "I'm en route",        icon: FaRoute },
  { code: 'arrived',            label: 'Arrived',      cta: "I've arrived",        icon: FaMapPin },
  { code: 'successful_draw',    label: 'Drawn',        cta: 'Log draw result',     icon: FaSyringe, isDraw: true },
  { code: 'spun',               label: 'Spin blood',   cta: 'Spin blood + photo',  icon: FaVial, isSpin: true },
  // The drop itself is done as a batch from the "Ready to drop" list — never a
  // manual button here. This step only shows as the final rail stage.
  { code: 'dropped_off_lab',    label: 'Dropped off',  cta: 'Dropped off at lab',  icon: FaBox, batchDrop: true },
];
const STEP_CODES = VISIT_STEPS.map((s) => s.code);
// Legacy: some draws sit at 'transit_to_dropoff' (an old intermediate step).
// Treat it as 'spun' for the visit flow so it lands on "ready for drop".
const flowCode = (code) => (code === 'transit_to_dropoff' ? 'spun' : code);

// The common non-happy-path results the phleb can pick right on the draw step,
// without opening the full outcome list.
const DRAW_ALT_OUTCOMES = ['partial_draw', 'no_show', 'patient_refused', 'reschedule_requested'];

// ── Small display helpers ───────────────────────────────────────────────────────

// Strip non-dial characters so tel:/sms: URIs work on "(555) 123-4567"-style input.
const dialDigits = (phone) => {
  if (!phone) return null;
  const cleaned = String(phone).replace(/[^\d+]/g, '');
  return cleaned || null;
};

// Prefer the single address field; fall back to the split columns.
const fmtAddress = (s) => {
  if (!s) return '';
  if (s.patient_address) return s.patient_address;
  return [s.patient_street, s.patient_city, s.patient_state, s.patient_zip]
    .filter(Boolean)
    .join(', ');
};

const getStatusColor = (status) => getSubmissionStatusColor(status);

const getStatusLabel = (status) => {
  const map = {
    completed: 'Completed', in_progress: 'In Progress', pending: 'Pending',
    cancelled: 'Cancelled', waiting_on_lab_results: 'Dropped at Lab', waiting_to_be_received: 'In Transit',
    needs_reschedule: 'Needs Reschedule',
  };
  return map[status] || (status ? status.replace(/_/g, ' ') : 'Unknown');
};

const formatDateTime = (d) => {
  if (!d) return null;
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(d); }
};

const formatDate = (d) => {
  if (!d) return '—';
  try { const dt = new Date(d); return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(); } catch { return d; }
};

// ── Main Component ──────────────────────────────────────────────────────────────

export default function LabDrawSummary() {
  const { id, submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testDetails, setTestDetails] = useState([]);
  const [latestNote, setLatestNote] = useState(null); // most recent status-event note
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // synchronous double-tap guard
  const [drawPanelOpen, setDrawPanelOpen] = useState(false); // inline draw-result panel
  const [drawNote, setDrawNote] = useState('');
  const [spinPanelOpen, setSpinPanelOpen] = useState(false);  // spin-blood + photo panel
  const [spinUploading, setSpinUploading] = useState(false);
  const [spinPhotoUrl, setSpinPhotoUrl] = useState('');
  // LabAide tube guide per test (keyed by test id) + confirm-save state.
  const [tubeGuide, setTubeGuide] = useState(null); // single combined LabAide result
  const [tubeStepSaving, setTubeStepSaving] = useState(null); // step key currently saving
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isOutcomeOpen, onOpen: openOutcome, onClose: closeOutcome } = useDisclosure();
  // "Did you contact the patient?" gate before going en route.
  const { isOpen: contactOpen, onOpen: openContact, onClose: closeContact } = useDisclosure();
  const [contactStep, setContactStep] = useState('ask'); // 'ask' | 'note' | 'callFirst'
  const [contactNote, setContactNote] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  const [previewSpin, setPreviewSpin] = useState(false); // tour shows the spin panel even when not at that step
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const submissionIdValue = isNaN(parseInt(submissionId)) ? submissionId : parseInt(submissionId);
        const { data, error } = await supabase
          .from('submissions')
          .select('*')
          .eq('id', submissionIdValue)
          .single();
        if (error) { console.error('Error fetching submission:', error); setLoading(false); return; }
        setSubmission(data);
      } catch (error) {
        console.error('Error fetching submission:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [submissionId]);

  // Fetch test/panel names for the ordered tests.
  useEffect(() => {
    const fetchTestDetails = async () => {
      const tests = Array.isArray(submission?.tests_on_the_script) ? submission.tests_on_the_script : [];
      const ids = tests.map(t => t.id).filter(Boolean);
      if (!ids.length) { setTestDetails([]); return; }
      const [{ data: testsData }, { data: panelsData }] = await Promise.all([
        supabase.from('tests').select('id, name, code').in('id', ids),
        supabase.from('panels').select('id, name, code').in('id', ids),
      ]);
      setTestDetails([
        ...(testsData || []).map(t => ({ id: t.id, name: t.name, code: t.code })),
        ...(panelsData || []).map(p => ({ id: p.id, name: p.name, code: p.code })),
      ]);
    };
    if (submission) fetchTestDetails();
  }, [submission?.tests_on_the_script]);

  // Pull the LabAide collection guide for ALL ordered tests in ONE request —
  // a single combined query returns one consolidated answer (instead of a
  // separate API call per test). Cached per test-set.
  useEffect(() => {
    // Build the guide from the ordered tests. Prefer the name/code stored inline
    // on the script (keeps LDX-imported tests whose ids aren't in the tests/panels
    // tables), and fall back to the resolved testDetails by id (for orders that
    // store only a test id — e.g. Quest/manual orders). Using either source alone
    // drops whichever kind of order isn't represented there.
    const scriptTests = Array.isArray(submission?.tests_on_the_script) ? submission.tests_on_the_script : [];
    const byId = new Map((testDetails || []).map((t) => [t.id, t]));
    const items = scriptTests
      .map((t) => {
        const resolved = t.id != null ? byId.get(t.id) : null;
        return { name: t.name || resolved?.name, code: t.code || resolved?.code };
      })
      .filter((t) => t.name || t.code);
    if (items.length === 0) { setTubeGuide(null); return; }
    // Instant load: use the cached guide when the ordered test set is unchanged.
    const sig = tubeGuideSig(scriptTests);
    const cached = submission?.tube_guide;
    if (cached && cached.sig === sig && cached.result) { setTubeGuide(cached.result); return; }
    const apiBase = process.env.REACT_APP_API_URL || '';
    let cancelled = false;
    setTubeGuide({ status: 'loading' });
    (async () => {
      const result = await lookupTubesForTests(items, { apiBase, source: 'phleb-draw' });
      if (cancelled) return;
      setTubeGuide(result);
      // Persist so the next open (phleb or admin) is instant — good results only.
      if (result && result.status !== 'error' && submission?.id) {
        supabase.from('submissions').update({ tube_guide: { sig, result } }).eq('id', submission.id).then(() => {}, () => {});
      }
    })();
    return () => { cancelled = true; };
  }, [submission?.tests_on_the_script, testDetails]);

  // Pull the most recent disposition note so the phleb sees the latest word on
  // this draw (e.g. "patient asked to reschedule for Wednesday 3pm").
  useEffect(() => {
    const fetchLatestNote = async () => {
      if (!submission?.id) return;
      const { data } = await supabase
        .from('submission_status_events')
        .select('outcome_code, outcome_notes, logged_at, logged_by_kind')
        .eq('submission_id', submission.id)
        .not('outcome_notes', 'is', null)
        .order('logged_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setLatestNote(data && data.outcome_notes ? data : null);
    };
    fetchLatestNote();
  }, [submission?.id]);

  // Continuation of the onboarding tour — the list-page tour deep-links here
  // with ?tour=1 so the phleb sees a real patient page. Strip the param after.
  // Must run before the early returns below to keep hook order stable.
  useEffect(() => {
    if (searchParams.get('tour') === '1' && submission) {
      setTourOpen(true);
      const p = new URLSearchParams(searchParams);
      p.delete('tour');
      setSearchParams(p, { replace: true });
    }
  }, [searchParams, setSearchParams, submission]);

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.500" fontSize="sm">Loading draw…</Text>
        </VStack>
      </Flex>
    );
  }

  if (!submission) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="gray.50">
        <Text color="gray.500">Submission not found.</Text>
      </Flex>
    );
  }

  const accession = submission.draw_code || (submission.id ? String(submission.id).slice(-8) : '—');
  const address = fmtAddress(submission);
  const dial = dialDigits(submission.patient_phone);
  const appointment = formatSchedule(submission) || formatDateTime(submission.blood_collection_time);
  const directionsUrl = address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
    : null;
  const tags = Array.isArray(submission.tags) ? submission.tags : [];
  const noteOutcome = latestNote?.outcome_code ? getOutcomeConfig(latestNote.outcome_code) : null;
  const handleOutcomeLogged = (updated) => {
    setSubmission((prev) => ({ ...prev, ...updated }));
    setDrawPanelOpen(false);
    setDrawNote('');
  };

  // ── Visit progression state ──────────────────────────────────────────────
  // Figure out where this draw is along the happy path and what the next tap does.
  const currentCode = submission.outcome_code || null;
  const currentCfg = currentCode ? getOutcomeConfig(currentCode) : null;
  const currentIdx = STEP_CODES.indexOf(flowCode(currentCode));
  let nextIdx;
  if (currentIdx >= 0) {
    nextIdx = currentIdx + 1; // advance from a known step (may run off the end → complete)
  } else if (!currentCfg || currentCfg.phase === 'intake' || currentCfg.phase === 'contact') {
    nextIdx = 0; // haven't started the visit yet → first step is "en route"
  } else {
    nextIdx = -2; // an exception visit/delivery outcome was logged — off the happy path
  }
  const nextStep = nextIdx >= 0 ? VISIT_STEPS[nextIdx] || null : null;
  const flowComplete = currentIdx === VISIT_STEPS.length - 1;
  const offPath = nextIdx === -2;

  // Detail-page tour steps, anchored to the real visit UI.
  const detailTourSteps = [
    { selector: '[data-tour="visit-steps"]', title: 'The visit, step by step',
      body: 'This rail tracks the whole visit: En route → Arrived → Drawn → Spin → Ready for drop → Dropped. It fills in green as you go.' },
    { selector: '[data-tour="primary-action"]', title: 'One big button',
      body: 'This is always your next action. Tap it to move the visit forward — no hunting through menus.' },
    { selector: '[data-tour="primary-action"]', title: 'Heading out',
      body: 'Tapping “En route” asks if you contacted the patient. Say yes and leave a quick note on what they said; the admin sees it.' },
    { selector: '#tube-guide-section', title: 'Tube guide',
      body: 'Scroll here and check off every tube as you draw it. The “Drawn” step stays locked until all are ticked.' },
    { selector: '[data-tour="primary-action"]', title: 'Mark the draw', before: () => setPreviewSpin(false),
      body: 'Once tubes are done, mark Successful (add flags like difficult stick) or Unsuccessful with a note.' },
    { selector: '[data-tour="spin-panel"]', title: 'Spin, label & photograph', before: () => setPreviewSpin(true),
      body: 'This is the spin step. Label the tube with the Order ID + patient name + DOB shown here, then take the required photo before tapping “Mark ready for drop.”' },
    { selector: '[data-tour="drop-at"]', title: 'Where it goes', before: () => setPreviewSpin(false),
      body: 'Once admin assigns a drop spot, this green box tells you exactly where to take (or hand off) the specimen. Then use “Ready to drop → Drop batch” back on your list.' },
    { title: 'That’s the visit!', before: () => setPreviewSpin(false),
      body: 'You’ve seen the full patient page. Head back to your list to start working real draws. You can reopen either tour anytime.' },
  ];

  // Log an outcome and advance — used by every one-tap step button.
  const advanceTo = async (code, note) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const { updateFields } = await logSubmissionOutcome({ submission, code, note, phlebotomistId: id });
      setSubmission((prev) => ({ ...prev, ...updateFields }));
      setDrawPanelOpen(false);
      setDrawNote('');
      // Refresh the latest-note callout when a note was attached.
      if (note) setLatestNote({ outcome_code: code, outcome_notes: note, logged_by_kind: 'phleb' });
      toast({ title: `Updated: ${getOutcomeConfig(code)?.label || code}`, status: 'success', duration: 2000 });
    } catch (e) {
      toast({ title: 'Could not update', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Persist a per-tube checkoff onto the submission so the field + admins can see
  // how far along the collection is. Each step (tube / urine / stool / swab) gets
  // its own entry in tube_guide_progress; once EVERY step is checked we also set
  // the legacy tube_guide_confirmed_at "all done" marker (and log a timeline
  // line), and we clear it again if a step is later unchecked.
  const toggleTubeStep = async (stepKey, nextChecked) => {
    if (!stepKey || tubeStepSaving) return;
    setTubeStepSaving(stepKey);
    try {
      const nowIso = new Date().toISOString();
      const prevProgress = submission.tube_guide_progress || {};
      const nextProgress = { ...prevProgress };
      if (nextChecked) nextProgress[stepKey] = { at: nowIso, by: id || null };
      else delete nextProgress[stepKey];

      // Was this the last step? Compare against the parsed guide's step set.
      const allKeys = (parseTubeGuide(tubeGuide?.bullets).steps || []).map((s) => s.key);
      const allDone =
        allKeys.length > 0 && allKeys.every((k) => nextProgress[k]?.at);
      const wasAllDone = !!submission.tube_guide_confirmed_at;

      const update = { tube_guide_progress: nextProgress };
      if (allDone && !wasAllDone) {
        update.tube_guide_confirmed_at = nowIso;
        update.tube_guide_confirmed_by = id || null;
      } else if (!allDone && wasAllDone) {
        update.tube_guide_confirmed_at = null;
        update.tube_guide_confirmed_by = null;
      }

      const { error } = await supabase
        .from('submissions')
        .update(update)
        .eq('id', submission.id);
      if (error) throw error;
      setSubmission((prev) => ({ ...prev, ...update }));

      // When the whole draw is collected, drop a line in the activity timeline so
      // admins see it in the draw's history. Best-effort; carries no outcome_code
      // so it doesn't change the draw's disposition or SLA clock.
      if (allDone && !wasAllDone) {
        const { error: evErr } = await supabase
          .from('submission_status_events')
          .insert({
            submission_id: submission.id,
            outcome_code: null,
            outcome_notes: 'All tubes collected — tube guide complete',
            status: submission.status || null,
            logged_by: id || null,
            logged_by_kind: 'phleb',
          });
        if (evErr) console.warn('Could not write tube-complete status event:', evErr.message);
        toast({ title: 'All tubes collected', status: 'success', duration: 2000 });
      }
    } catch (e) {
      toast({ title: 'Could not save', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setTubeStepSaving(null);
    }
  };

  // Tube-guide gate for "Drawn": every tube must be checked off first (when a
  // guide exists). Blocks logging the draw until all boxes are ticked.
  const tubeSteps = parseTubeGuide(tubeGuide?.bullets).steps || [];
  const allTubesChecked = tubeSteps.length === 0 || tubeSteps.every((s) => submission?.tube_guide_progress?.[s.key]?.at);

  // Spin-blood: upload the labeled-specimen photo, then persist + log "spun"
  // (ready for drop). Photo is required.
  const uploadSpinPhoto = async (file) => {
    if (!file) return;
    setSpinUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `spin-photos/${submission.id}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('uploads').upload(path, file, { contentType: file.type || 'image/jpeg', upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('uploads').getPublicUrl(path);
      setSpinPhotoUrl(data.publicUrl);
      toast({ title: 'Photo added', status: 'success', duration: 1500 });
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSpinUploading(false);
    }
  };

  const confirmSpun = async () => {
    if (!spinPhotoUrl) { toast({ title: 'Take a photo of the labeled specimen first', status: 'warning', duration: 3000 }); return; }
    try {
      await supabase.from('submissions')
        .update({ spin_photo_url: spinPhotoUrl, ready_for_drop_at: new Date().toISOString() })
        .eq('id', submission.id);
    } catch (_) { /* non-fatal; the outcome log below is the source of truth */ }
    setSpinPanelOpen(false);
    await advanceTo('spun', 'Blood spun; labeled specimen photographed. Ready for drop.');
    setSpinPhotoUrl('');
  };

  const onPrimaryStep = () => {
    if (!nextStep) return;
    if (nextStep.batchDrop) return; // dropping happens from the Ready to drop list, not here
    if (nextStep.isDraw) {
      if (!allTubesChecked) {
        toast({ title: 'Check off every tube first', description: 'Mark each tube in the guide as collected before logging the draw.', status: 'warning', duration: 3500, isClosable: true });
        document.getElementById('tube-guide-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      setDrawPanelOpen((v) => !v);
      return;
    }
    // Before going en route, confirm the patient was contacted.
    if (nextStep.code === 'en_route') { setContactStep('ask'); openContact(); return; }
    if (nextStep.isSpin) { setSpinPanelOpen((v) => !v); return; }
    advanceTo(nextStep.code);
  };

  return (
    <Box minH="100vh" bg="gray.50" pb={24}>
      {/* Contact-patient gate before going en route */}
      <Modal isOpen={contactOpen} onClose={closeContact} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Before you head out</ModalHeader>
          <ModalBody pb={2}>
            {contactStep === 'ask' && (
              <Text>Did you contact the patient to let them know you're on the way?</Text>
            )}
            {contactStep === 'note' && (
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" fontWeight="600">What did the patient say?</Text>
                <Textarea
                  value={contactNote}
                  onChange={(e) => setContactNote(e.target.value)}
                  placeholder="e.g. Confirmed they're home; gate code 1234; asked me to call on arrival…"
                  rows={3}
                  autoFocus
                />
              </VStack>
            )}
            {contactStep === 'callFirst' && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">Please call the patient first so they know you're coming, then mark yourself en route.</Text>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            {contactStep === 'ask' && (
              <HStack spacing={3}>
                <Button variant="outline" onClick={() => setContactStep('callFirst')}>No, not yet</Button>
                <Button colorScheme="teal" onClick={() => setContactStep('note')}>Yes, I contacted them</Button>
              </HStack>
            )}
            {contactStep === 'note' && (
              <HStack spacing={3}>
                <Button variant="ghost" onClick={() => setContactStep('ask')}>Back</Button>
                <Button
                  colorScheme="teal"
                  isDisabled={!contactNote.trim()}
                  isLoading={saving}
                  onClick={() => {
                    const note = contactNote.trim();
                    closeContact();
                    setContactNote('');
                    advanceTo('en_route', `Contacted patient — ${note}`);
                  }}
                >
                  I'm en route
                </Button>
              </HStack>
            )}
            {contactStep === 'callFirst' && (
              <HStack spacing={3}>
                <Button variant="ghost" onClick={closeContact}>Close</Button>
                {dial && (
                  <Button as="a" href={`tel:${dial}`} colorScheme="green" leftIcon={<Icon as={FaPhone} />}>
                    Call patient
                  </Button>
                )}
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.md" py={3}>
          <Flex justify="space-between" align="center" gap={2}>
            <HStack spacing={2} minW={0}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                size="sm"
                aria-label="Back to dashboard"
                onClick={() => navigate(`/phlebotomist/${id}`)}
              />
              <ChakraImage src="/logo.png" alt="AtHomeBloodDraw.com" h={8} objectFit="contain" />
            </HStack>
            <Box textAlign="right" minW={0}>
              <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{submission.patient_name}</Text>
              <HStack spacing={2} justify="flex-end">
                <Badge colorScheme={getStatusColor(submission.status)} fontSize="2xs" borderRadius="full" px={2}>
                  {getStatusLabel(submission.status)}
                </Badge>
                <Text fontSize="xs" color="gray.400" fontFamily="mono">{accession}</Text>
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <Container maxW="container.md" py={5}>
        <VStack spacing={5} align="stretch">

          {/* ⓪ Visit progression — the one big action, on top */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" p={5}>
            {/* Step rail */}
            <Flex data-tour="visit-steps" align="center" justify="space-between" mb={4}>
              {VISIT_STEPS.map((step, i) => {
                const done = currentIdx >= 0 && i <= currentIdx;
                const active = i === nextIdx;
                const color = done ? 'green' : active ? 'blue' : 'gray';
                return (
                  <React.Fragment key={step.code}>
                    <VStack spacing={1} flex="0 0 auto" minW={0}>
                      <Flex
                        w={9} h={9} borderRadius="full" align="center" justify="center" flexShrink={0}
                        bg={done ? 'green.500' : active ? 'blue.500' : 'gray.100'}
                        color={done || active ? 'white' : 'gray.400'}
                        border={active ? '2px solid' : 'none'} borderColor="blue.200"
                      >
                        <Icon as={done ? FaCheck : step.icon} boxSize={3.5} />
                      </Flex>
                      <Text fontSize="2xs" fontWeight={active ? '700' : '500'} color={`${color}.600`} textAlign="center" noOfLines={1}>
                        {step.label}
                      </Text>
                    </VStack>
                    {i < VISIT_STEPS.length - 1 && (
                      <Box flex={1} h="2px" bg={currentIdx > i ? 'green.300' : 'gray.100'} mx={1} mt={-4} />
                    )}
                  </React.Fragment>
                );
              })}
            </Flex>

            {/* Primary action */}
            {flowComplete ? (
              <Flex align="center" justify="center" gap={2} bg="green.50" borderRadius="lg" py={3}>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text fontWeight="700" color="green.700">Draw complete — dropped off</Text>
              </Flex>
            ) : offPath ? (
              <VStack align="stretch" spacing={2}>
                <Flex align="center" justify="center" gap={2} bg="orange.50" borderRadius="lg" py={2}>
                  <Badge colorScheme={currentCfg?.color || 'orange'} borderRadius="full">{currentCfg?.label || 'Update needed'}</Badge>
                </Flex>
                <Button size="lg" colorScheme="blue" leftIcon={<Icon as={FaClipboardCheck} />} onClick={openOutcome} isLoading={saving}>
                  Log next update
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setContactStep('ask'); openContact(); }} isDisabled={saving}>
                  Restart visit (I'm en route)
                </Button>
              </VStack>
            ) : nextStep && nextStep.batchDrop ? (
              // Spun & ready — the drop is done as a batch from the list, not here.
              <VStack align="stretch" spacing={2} data-tour="primary-action">
                <Flex align="center" gap={2} bg="cyan.50" borderRadius="lg" p={3} border="1px solid" borderColor="cyan.200">
                  <Icon as={FaCheckCircle} color="cyan.500" boxSize={5} flexShrink={0} />
                  <Box>
                    <Text fontWeight="700" color="cyan.800" fontSize="sm">Ready for drop</Text>
                    <Text fontSize="xs" color="cyan.700">Spun & photographed. Drop it from the “Ready to drop” list with the rest of your batch.</Text>
                  </Box>
                </Flex>
                <Button size="md" variant="outline" colorScheme="cyan" leftIcon={<Icon as={FaVial} />}
                  onClick={() => navigate(`/phlebotomist/${id}`)}>
                  Go to Ready to drop
                </Button>
              </VStack>
            ) : nextStep ? (
              <VStack align="stretch" spacing={2}>
                <Button
                  data-tour="primary-action"
                  size="lg"
                  h={14}
                  colorScheme={nextStep.isDraw ? 'teal' : 'blue'}
                  leftIcon={<Icon as={nextStep.icon} />}
                  onClick={onPrimaryStep}
                  isLoading={saving && !drawPanelOpen}
                >
                  {nextStep.cta}
                </Button>

                {/* Inline draw-result panel — note + the common outcomes, no hunting */}
                {nextStep.isDraw && drawPanelOpen && (
                  <Box mt={1} p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.100">
                    <Text fontSize="sm" fontWeight="600" color="gray.700" mb={2}>Add a note (optional)</Text>
                    <Textarea
                      value={drawNote}
                      onChange={(e) => setDrawNote(e.target.value)}
                      placeholder="e.g. 2 tubes drawn, left arm…"
                      size="sm"
                      bg="white"
                      mb={3}
                      rows={2}
                    />
                    <Button
                      w="full" size="lg" colorScheme="green" mb={3}
                      leftIcon={<Icon as={FaCheck} />}
                      onClick={() => advanceTo('successful_draw', drawNote)}
                      isLoading={saving}
                    >
                      Successful draw
                    </Button>
                    <Text fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Other result</Text>
                    <SimpleGrid columns={2} spacing={2}>
                      {DRAW_ALT_OUTCOMES.map((code) => {
                        const cfg = getOutcomeConfig(code);
                        return (
                          <Button
                            key={code} size="sm" variant="outline" colorScheme={cfg?.color || 'gray'}
                            onClick={() => advanceTo(code, drawNote)} isDisabled={saving}
                            whiteSpace="normal" h="auto" py={2}
                          >
                            {cfg?.label || code}
                          </Button>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Spin-blood panel — label the specimen, photograph it, ready for drop.
                    Also rendered (read-only) when the tour previews this step so the
                    phleb can see what it looks like before they're actually here. */}
                {(() => {
                  const liveSpin = nextStep.isSpin && spinPanelOpen;
                  const previewOnly = previewSpin && !liveSpin;
                  if (!liveSpin && !previewOnly) return null;
                  return (
                  <Box data-tour="spin-panel" mt={1} p={4} bg="cyan.50" borderRadius="lg" border="1px solid" borderColor="cyan.200">
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="sm" fontWeight="700" color="gray.700">
                        Spin the blood, then photograph the labeled specimen.
                      </Text>
                      {previewOnly && <Badge colorScheme="purple" flexShrink={0}>Preview</Badge>}
                    </HStack>
                    {/* What to write on the tube */}
                    <Box bg="white" borderWidth="2px" borderColor="cyan.400" borderRadius="md" p={3} mb={3}>
                      <Text fontSize="2xs" color="gray.500" textTransform="uppercase" fontWeight="800" letterSpacing="wider" mb={1.5}>
                        Label the specimen with:
                      </Text>
                      <VStack align="stretch" spacing={1}>
                        <HStack justify="space-between"><Text fontSize="xs" color="gray.500">Order ID</Text><Text fontWeight="800" fontFamily="mono">{submission.lab_order_number || '—'}</Text></HStack>
                        <HStack justify="space-between"><Text fontSize="xs" color="gray.500">Patient</Text><Text fontWeight="700">{submission.patient_name || '—'}</Text></HStack>
                        <HStack justify="space-between"><Text fontSize="xs" color="gray.500">DOB</Text><Text fontWeight="700">{submission.patient_dob || '—'}</Text></HStack>
                      </VStack>
                    </Box>
                    {spinPhotoUrl && !previewOnly ? (
                      <VStack spacing={2} mb={3}>
                        <ChakraImage src={spinPhotoUrl} alt="Spun specimen" maxH="220px" borderRadius="md" objectFit="contain" />
                        <Button size="sm" variant="outline" onClick={() => setSpinPhotoUrl('')}>Retake photo</Button>
                      </VStack>
                    ) : previewOnly ? (
                      <Button w="full" colorScheme="cyan" leftIcon={<Icon as={FaVial} />} mb={3} isDisabled>
                        Take photo of specimen
                      </Button>
                    ) : (
                      <Button as="label" htmlFor="spin-photo-input" w="full" colorScheme="cyan" leftIcon={<Icon as={FaVial} />} isLoading={spinUploading} cursor="pointer" mb={3}>
                        Take photo of specimen
                        <input id="spin-photo-input" type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => uploadSpinPhoto(e.target.files?.[0])} />
                      </Button>
                    )}
                    <Button w="full" size="lg" colorScheme="teal" leftIcon={<Icon as={FaCheck} />} isDisabled={previewOnly || !spinPhotoUrl} isLoading={saving} onClick={confirmSpun}>
                      Mark ready for drop
                    </Button>
                  </Box>
                  );
                })()}

                <Button size="sm" variant="ghost" color="gray.500" leftIcon={<Icon as={FaEllipsisH} boxSize={3} />} onClick={openOutcome}>
                  More options
                </Button>
              </VStack>
            ) : null}
          </Box>

          {/* ① Visit card — who, where, when */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" p={5}>
            <Flex justify="space-between" align="start" gap={3} mb={3} flexWrap="wrap">
              <Box minW={0}>
                <Heading size="lg" color="gray.800" noOfLines={2}>{submission.patient_name || 'Unnamed patient'}</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>DOB {formatDate(submission.patient_dob)}</Text>
                {submission.lab_order_number && (
                  <Text fontSize="sm" color="blue.700" fontWeight="700" mt={1}>Order #{submission.lab_order_number}</Text>
                )}
                {submission.order_date && (
                  <Text fontSize="xs" color="gray.500" mt={0.5}>Ordered {formatDate(submission.order_date)}</Text>
                )}
              </Box>
              {appointment && (
                <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="lg" px={3} py={2} textAlign="right">
                  <HStack spacing={1.5} color="blue.700" justify="flex-end">
                    <Icon as={FaCalendarAlt} boxSize={3} />
                    <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider">Appointment</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="bold" color="blue.800" mt={0.5}>{appointment}</Text>
                </Box>
              )}
            </Flex>

            {/* Tags & flags */}
            {(tags.length > 0 || submission.stat_test) && (
              <Flex gap={2} flexWrap="wrap" mb={4}>
                {submission.stat_test && (
                  <Badge colorScheme="red" borderRadius="full" px={3} py={1}>STAT</Badge>
                )}
                {tags.map((t) => (
                  <Badge key={t} colorScheme={tagColor(t)} borderRadius="full" px={3} py={1}>{t}</Badge>
                ))}
              </Flex>
            )}

            {/* Address + Directions */}
            <Box mb={4}>
              <Text fontSize="xs" color="gray.500" mb={1}>Address</Text>
              <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
                <HStack spacing={2} align="start" minW={0}>
                  <Icon as={FaMapMarkerAlt} boxSize={3.5} color="gray.400" mt={1} />
                  <Text fontSize="sm" fontWeight="medium" color="gray.800">{address || '—'}</Text>
                </HStack>
                {directionsUrl && (
                  <Button
                    as="a"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Icon as={FaLocationArrow} boxSize={3} />}
                    flexShrink={0}
                  >
                    Directions
                  </Button>
                )}
              </Flex>
            </Box>

            {/* Drop destination — assigned by admin, read-only for the phleb.
                Shows once a drop spot has been set on this specimen. */}
            {submission.drop_location && (
              <Box data-tour="drop-at" mb={4} bg="green.50" border="1px solid" borderColor="green.200" borderRadius="lg" p={3}>
                <HStack spacing={1.5} color="green.700" mb={1}>
                  <Icon as={FaLocationArrow} boxSize={3} />
                  <Text fontSize="2xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider">Drop specimen at</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="700" color="green.900">{submission.drop_location}</Text>
              </Box>
            )}

            {/* Phone + Call / Text */}
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Phone</Text>
              {dial ? (
                <Flex align="center" gap={3} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="medium" color="gray.800">{submission.patient_phone}</Text>
                  <HStack spacing={2}>
                    <Button as="a" href={`tel:${dial}`} size="sm" colorScheme="green" leftIcon={<Icon as={FaPhone} boxSize={3} />}>
                      Call
                    </Button>
                    <Button as="a" href={`sms:${dial}`} size="sm" colorScheme="blue" variant="outline" leftIcon={<Icon as={FaSms} boxSize={3} />}>
                      Text
                    </Button>
                  </HStack>
                </Flex>
              ) : (
                <Text fontSize="sm" fontWeight="medium" color="gray.800">{'—'}</Text>
              )}
            </Box>
          </Box>

          {/* Latest note — most recent word on this draw */}
          {(latestNote?.outcome_notes || submission.special_instructions) && (
            <Box bg="orange.50" borderRadius="xl" border="1px solid" borderColor="orange.100" p={4}>
              <HStack spacing={2} mb={1}>
                <Icon as={FaStickyNote} boxSize={3.5} color="orange.500" />
                <Text fontSize="xs" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="wider">
                  Latest note
                </Text>
                {noteOutcome && (
                  <Badge colorScheme={noteOutcome.color} fontSize="2xs" borderRadius="full">{noteOutcome.label}</Badge>
                )}
              </HStack>
              {latestNote?.outcome_notes && (
                <Text fontSize="sm" color="gray.800">"{latestNote.outcome_notes}"</Text>
              )}
              {submission.special_instructions && (
                <Text fontSize="sm" color="gray.700" mt={latestNote?.outcome_notes ? 2 : 0}>
                  {submission.special_instructions}
                </Text>
              )}
            </Box>
          )}

          {/* ② What to draw */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" p={5}>
            <HStack spacing={2} mb={3}>
              <Flex w={8} h={8} borderRadius="lg" bg="teal.50" align="center" justify="center" flexShrink={0}>
                <Icon as={FaVial} boxSize={4} color="teal.500" />
              </Flex>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" textTransform="uppercase" letterSpacing="wider">
                What to draw
              </Text>
              {testDetails.length > 0 && (
                <Badge colorScheme="teal" fontSize="xs" borderRadius="full" px={2}>{testDetails.length}</Badge>
              )}
            </HStack>

            {testDetails.length > 0 ? (
              <Flex gap={2} flexWrap="wrap" mb={4}>
                {testDetails.map((t) => (
                  <Badge key={t.id} colorScheme="teal" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs">
                    {t.name}{t.code ? ` (${t.code})` : ''}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <Text fontSize="sm" color="gray.500" mb={4}>
                No structured test list — check the script below.
              </Text>
            )}

            {/* Test Tube Guide — one checkable panel per draw-order tube, so the
                phleb ticks each off as they collect it and the progress is saved
                for the admin to see. */}
            {tubeGuide && (
              <Box mb={4} id="tube-guide-section">
                <TubeGuide
                  result={tubeGuide}
                  progress={submission.tube_guide_progress || {}}
                  onToggleStep={toggleTubeStep}
                  savingKey={tubeStepSaving}
                />
              </Box>
            )}

            {submission.doctor_name && (
              <HStack spacing={2} mb={4} color="gray.500">
                <Icon as={FaUserMd} boxSize={3} />
                <Text fontSize="xs">Ordered by {submission.doctor_name}</Text>
              </HStack>
            )}

            <Divider mb={4} />

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              <Box>
                <HStack spacing={1.5} mb={2}>
                  <Icon as={FaFileAlt} boxSize={3} color="gray.400" />
                  <Text fontSize="xs" color="gray.500">Doctor's Script</Text>
                </HStack>
                <DocumentPreview
                  urls={submission.script_image || submission.script_url}
                  title="Doctor's Script"
                  size="md"
                />
              </Box>
              <Box>
                <HStack spacing={1.5} mb={2}>
                  <Icon as={FaUser} boxSize={3} color="gray.400" />
                  <Text fontSize="xs" color="gray.500">Patient ID</Text>
                </HStack>
                <DocumentPreview
                  urls={submission.patient_id_image || submission.patient_id_url}
                  title="Patient ID"
                  size="md"
                />
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <OutcomeDrawer
        isOpen={isOutcomeOpen}
        onClose={closeOutcome}
        submission={submission}
        phlebotomistId={id}
        onLogged={handleOutcomeLogged}
      />

      <PhlebTour
        steps={detailTourSteps}
        isOpen={tourOpen}
        onClose={() => { setTourOpen(false); setPreviewSpin(false); }}
        onComplete={async () => {
          try {
            await supabase.from('phleb_training_ack').insert({
              full_name: submission?.phlebotomist_name || 'Phleb',
              phlebotomist_id: id || null,
              user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            });
          } catch (e) { /* logging shouldn't block the tour */ }
          toast({ title: 'Onboarding tour complete — you’re ready!', status: 'success', duration: 3000, isClosable: true });
        }}
      />
    </Box>
  );
}
